import { useState } from "react";
import { Link } from "react-router-dom";
import { SeoHead } from "../components/SeoHead";
import { PremiumGate } from "../components/PremiumGate";
import { Plus, Trash2, ShoppingCart, Share2, CalendarDays, RefreshCw } from "lucide-react";
import { track } from "../lib/analytics";
import { loadGrocery, saveGrocery, loadPlan, generateFromPlan, DAYS } from "../lib/planner";

const initialItems = () => {
  const items = loadGrocery();
  const plan = loadPlan();
  return items.length === 0 && DAYS.some((d) => plan[d]) ? generateFromPlan(plan) : items;
};

const AISLE_ORDER = ["Fresh", "Meat & Fish", "Dairy", "Bakery", "Cupboard", "Frozen", "Other"];

const GroceryRow = ({ it, onToggle, onRemove }) => (
  <div
    className={`flex items-center gap-3 border-2 p-3 transition-colors ${it.checked ? "border-brand-primary/40 bg-brand-primary/10" : "border-white/20"}`}
    data-testid={`grocery-item-${it.id}`}
  >
    <button
      type="button"
      onClick={() => onToggle(it.id)}
      data-testid={`grocery-toggle-${it.id}`}
      className={`w-5 h-5 shrink-0 border-2 flex items-center justify-center ${it.checked ? "bg-brand-primary border-brand-primary" : "border-white/60"}`}
      aria-label={it.checked ? "Untick" : "Tick"}
    />
    <span className="flex-1 min-w-0">
      <span className={`block font-mono text-sm ${it.checked ? "line-through text-foreground/40" : ""}`}>{it.text}</span>
      {it.for?.length > 0 && (
        <span className="block font-mono text-[10px] uppercase tracking-widest text-foreground/40">for {it.for.join(" · ")}</span>
      )}
    </span>
    <button type="button" onClick={() => onRemove(it.id)} data-testid={`grocery-remove-${it.id}`} className="text-foreground/40 hover:text-brand-danger" aria-label="Remove">
      <Trash2 size={14} />
    </button>
  </div>
);

export default function GroceryList() {
  const [items, setItems] = useState(initialItems);
  const [input, setInput] = useState("");

  const persist = (next) => { setItems(next); saveGrocery(next); };
  const toggle = (id) => persist(items.map((i) => (i.id === id ? { ...i, checked: !i.checked } : i)));
  const remove = (id) => persist(items.filter((i) => i.id !== id));
  const add = () => {
    const t = input.trim();
    if (!t) return;
    persist([...items, { id: `${Date.now()}`, text: t, checked: false, aisle: "Other", source: "manual" }]);
    setInput("");
  };
  const fillFromPlan = () => {
    setItems(generateFromPlan(loadPlan()));
    track("grocery_generated_from_plan");
  };
  const clearTicked = () => persist(items.filter((i) => !i.checked));

  const groups = AISLE_ORDER
    .map((aisle) => [aisle, items.filter((i) => (AISLE_ORDER.includes(i.aisle) ? i.aisle : "Other") === aisle)])
    .filter(([, list]) => list.length);

  const shareList = async () => {
    track("grocery_share_clicked");
    const text = items.filter((i) => !i.checked).map((i) => `• ${i.text}`).join("\n");
    const message = `My grocery list — Cheese on Toast:\n\n${text}`;
    try {
      if (navigator.share) await navigator.share({ title: "Grocery list", text: message });
      else if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(message);
    } catch { /* user cancelled share sheet */ }
  };

  return (
    <div data-testid="grocery-page" className="space-y-6">
      <SeoHead
        title="Grocery List — Cheese on Toast Premium"
        description="Smart shopping list grouped by aisle. Share to your phone before you go to the shop."
        canonicalPath="/grocery-list"
      />

      <header className="space-y-2 pt-1">
        <span className="label-tag">PREMIUM · GROCERY</span>
        <h1 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.95]">
          Grocery List
        </h1>
        <p className="font-mono text-sm text-foreground/70">Built from your meal plan. Sorted by aisle. Tap to tick.</p>
      </header>

      <PremiumGate title="Smart Grocery List" description="Premium feature — auto-syncs with your meal plan.">
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="grocery-fill-from-plan" onClick={fillFromPlan} className="btn-arcade px-3 py-2 text-xs">
            <RefreshCw size={14} /> Fill from my week
          </button>
          <Link to="/meal-plan" data-testid="grocery-edit-plan" className="btn-arcade btn-ghost px-3 py-2 text-xs">
            <CalendarDays size={14} /> Edit meal plan
          </Link>
        </div>

        <div className="brut-card p-3 flex gap-2" data-testid="grocery-add-row">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add item..."
            data-testid="grocery-input"
            className="flex-1 bg-ink border-2 border-white/30 p-2 font-mono text-sm focus:border-brand-primary outline-none"
          />
          <button type="button" data-testid="grocery-add" onClick={add} className="btn-arcade px-3 py-2">
            <Plus size={16} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="brut-card p-6 text-center space-y-2" data-testid="grocery-empty">
            <ShoppingCart size={28} className="mx-auto text-foreground/40" />
            <div className="font-display font-black uppercase text-base">List is empty</div>
            <p className="font-mono text-xs text-foreground/60">Tap "Fill from my week" to pull every ingredient from your meal plan.</p>
          </div>
        ) : (
          <section data-testid="grocery-groups" className="space-y-4">
            {groups.map(([aisle, list]) => (
              <div key={aisle} className="space-y-2" data-testid={`aisle-${aisle.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">{aisle}</div>
                {list.map((it) => <GroceryRow key={it.id} it={it} onToggle={toggle} onRemove={remove} />)}
              </div>
            ))}
          </section>
        )}

        <button type="button" data-testid="grocery-share" onClick={shareList} className="btn-arcade w-full">
          <Share2 size={18} /> Send To Phone
        </button>

        <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/40 flex items-center gap-3 justify-center w-full" data-testid="grocery-counts">
          <span className="inline-flex items-center gap-2"><ShoppingCart size={12} /> {items.filter((i) => !i.checked).length} to buy · {items.filter((i) => i.checked).length} done</span>
          {items.some((i) => i.checked) && (
            <button type="button" data-testid="grocery-clear-ticked" onClick={clearTicked} className="underline hover:text-brand-primary">Clear ticked</button>
          )}
        </div>
      </PremiumGate>
    </div>
  );
}
